// @flow
import {AccessTimeOutlined, CheckCircleOutlined, InfoOutlined} from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {TextField, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import React, {useMemo} from 'react';
import {NumericFormat} from "react-number-format";
import {Permissions} from "../../../../helpers/constants";
import useUserData from "../../../../hooks/user/useUserData";
import {useHasPermission} from "../../../../modules/permission/hooks/useHasPermission";
import {normalizeGreek} from "../../../../helpers/normalizeGreek";
import {useTranslation} from "react-i18next";
import {PRODUCT_DETAIL} from "../../../../locales/components/namespaces";

type Props = {};

const Warehouse = (props: Props) => {
    const {
        onQuantityChange,
        getDotsForWh,
        warehouse,
        quantity,
        index,
        handleAvailability,
        handleDots,
        setQuantity,
        dateFormat
    } = props
    const {thousandSeparator, decimalSeparator} = useUserData()
    const {t} = useTranslation(PRODUCT_DETAIL)

    const realStockNum = Number(warehouse.realStock) || 0;
    const quantityNum  = Number(warehouse.quantity) || 0;
    const isAdmin = useHasPermission([Permissions.AccessAdmin], {shouldView: true})

    const availability = useMemo(() => {
        if (realStockNum >= quantityNum) {
            return {
                tiresAvailable: quantityNum,
                tiresInWaiting: 0
            };
        } else if (realStockNum < quantityNum) {
            return {
                tiresAvailable: realStockNum,
                tiresInWaiting: quantityNum - realStockNum
            };
        }
        return {
            tiresAvailable: 0,
            tiresInWaiting: 0
        };
    }, [quantityNum, realStockNum]);

    const rawDots = getDotsForWh ? getDotsForWh(warehouse.id) : warehouse.dots;
    const availabilityDots = Array.isArray(rawDots)
        ? [...rawDots]
            .filter(Boolean)
            .map(d => ({
                DotYear: String(d?.DotYear ?? ''),
                Qty: Number(d?.Qty) || 0,
                DiscountPCT: Number(d?.DiscountPCT) || 0,
                displayStock: Number(d?.displayStock) || 0,
            }))
            .sort((a, b) => String(b.DotYear).localeCompare(String(a.DotYear)))
        : [];


    return (
        <Box
            pl={0.4}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                ...(index % 2 === 0 && {backgroundColor: 'grey.100'}),
                wordWrap: 'break-word'
            }}
        >
            <Box pr={0.1}
                 sx={{
                     width: {xs: '80px', md: '72px', lg: '115px'},
                     flexShrink: 0
                 }}
            >
                <Typography variant={'overline'} component={'div'}>
                    {warehouse.address}
                </Typography>
            </Box>
            <Box
                sx={{
                    color: `${warehouse.realStock > 0 ? 'green' : 'red'}`,
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '38px 16px 16px 28px 28px minmax(70px, 1fr)',
                        md: '34px 16px 16px 24px 24px minmax(82px, 1fr)',
                        lg: '50px 16px 21px 40px 40px minmax(90px, 1fr)'
                    },
                    alignItems: 'center',
                    flex: 1,
                    gap: {xs: 0.4, md: 0.15, lg: 0.15},
                    minWidth: 0
                }}
            >
                <Typography
                    variant={'body2'}
                    component={'div'}
                    sx={{
                        width: '100%',
                        flexShrink: 0,
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <Box
                        sx={{
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            width: '40px',
                            fontWeight: 'bold'
                        }}
                        pl={1}
                    >
                        {Number(warehouse.realStock) > 0 ? (
                                Number(warehouse.realStock) > Number(warehouse.displayStock) && !isAdmin ?
                                    `${Number(warehouse.displayStock)}+`
                                    : `${Number(warehouse.realStock)}`)
                            : 0}
                    </Box>
                </Typography>

                <InfoOutlined
                    color={'primary'}
                    sx={{width: '16px', height: '16px', justifySelf: 'center'}}
                    onMouseEnter={(evt) => handleAvailability({
                        anchorEl: evt.currentTarget,
                        availability: availability,
                        warehouse: warehouse
                    })}
                    onMouseLeave={(evt) => handleAvailability({
                        anchorEl: null,
                        availability: null,
                        warehouse: null
                    })}
                />
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {availabilityDots.length > 0 && (
                    <InfoOutlined
                        color="primary"
                        sx={{width: 16, height: 16}}
                        onMouseEnter={(evt) =>
                            handleDots({
                                anchorDotEl: evt.currentTarget,
                                dots: availabilityDots,
                            })
                        }
                        onMouseLeave={() =>
                            handleDots({
                                anchorDotEl: null,
                                dots: null,
                            })
                        }
                    />
                )}
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.2
                    }}
                >
                    {availability.tiresAvailable > 0 && (
                        <>
                            <Typography
                                variant={'body2'}
                                component={'div'}
                            >
                                {availability.tiresAvailable}
                            </Typography>
                            <CheckCircleOutlined sx={{width: '14px', height: '14px'}}/>
                        </>
                    )}
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.2
                    }}
                >
                    {availability.tiresInWaiting > 0 && (
                        <>
                            <Typography
                                variant={'body2'}
                                component={'div'}
                                sx={{color: 'orange'}}
                            >
                                {availability.tiresInWaiting}
                            </Typography>
                            <AccessTimeOutlined sx={{width: '14px', height: '14px', color: 'orange'}}/>
                        </>
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        minWidth: 0,
                        pr: 1
                    }}
                >
                    {warehouse?.expectedDate && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                            minWidth: 0,
                            fontStyle: 'italic',
                            justifyContent: 'center',
                            pr: '2px'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexWrap: 'nowrap',
                                whiteSpace: 'nowrap',
                            }}>
                                <Typography color={'primary'} variant={'caption'} component={'div'}
                                            className="expected-date"
                                            sx={{fontSize: 11, whiteSpace: 'nowrap'}}
                                            pr={0.4}>
                                    <sup>*</sup>
                                    {dayjs(warehouse?.expectedDate).format(dateFormat)}
                                </Typography>
                                <LocalShippingIcon color={'primary'}
                                                   className="expected-icon"
                                                   sx={{fontSize: 13, flexShrink: 0, mt: 0.1}}/>
                            </Box>
                            <Typography color={'primary'} variant={'caption'} component={'div'}
                                        sx={{
                                            fontSize: 9,
                                            mt: 0.2,
                                            whiteSpace: 'nowrap',
                                            textAlign: 'center',
                                        }}>
                                <Box component="span" className="expected-qty" sx={{fontSize: 11}}>
                                    {Number(warehouse?.expectedQty) > 20 ? '20+' : Number(warehouse?.expectedQty)}
                                </Box>
                                {' '}
                                {t('expected pieces')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    width: {xs: '72px', lg: '80px'},
                    flexShrink: 0
                }}
            >
                <NumericFormat
                    thousandSeparator={thousandSeparator}
                    decimalSeparator={decimalSeparator}
                    value={quantity?.find(f => String(f.id) === String(warehouse.id))?.quantity ?? 0}
                    onValueChange={(values) => {
                        const next = Number.isFinite(values.floatValue) ? values.floatValue : 0;
                        setQuantity(quantity.map((f) => ({
                            ...f,
                            quantity: String(f.id) === String(warehouse.id) ? next : f.quantity,
                        })));
                        if (typeof onQuantityChange === 'function') {
                            onQuantityChange(warehouse.id, next);
                        }
                    }}
                    decimalScale={0}
                    customInput={TextField}
                    size={'small'}
                    sx={{width: {xs: '56px', lg: '60px'}}}
                    type={'number'}
                    inputProps={{min: 0, style: {textAlign: 'center'}, type: 'number'}}
                />
            </Box>
        </Box>
    );
};

export default Warehouse